import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { useNotification } from '../../../context/NotificationContext';
import { NotificationType, Position } from '../../../types/enums/notificationEnums';
import clsx from 'clsx';
import Button from '../../common/Button';
import { useCart } from '../../../context/CartContext';

type CouponCodeProps = {
  onCouponApplied: (discount: number, couponId: number) => void;
  cartId: number | null;
};

const CouponCode: React.FC<CouponCodeProps> = ({ onCouponApplied, cartId }) => {
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isCouponValid, setIsCouponValid] = useState<boolean | null>(null);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const { calculateSubTotal } = useCart();
  const [error, setError] = useState('');

  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyCoupon();
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim() || !cartId) return;

    setIsCouponLoading(true);
    setError(''); // Clear previous errors
    const cartTotal = calculateSubTotal();

    try {
      // Updated API request with new parameters
      const response = await api.post('/coupons/apply', {
        couponCode: couponCode,
        cartTotal: cartTotal
      });

      // If we reach here, the request was successful
      const discount = response.data.data.discount || 0;
      const couponId = response.data.data.couponId;
      setIsCouponValid(true);
      setCouponDiscount(discount);
      onCouponApplied(discount, couponId);
      showNotification(
        t('Coupon applied successfully!'),
        NotificationType.Success,
        Position.TopRight
      );
    } catch (error: any) {
      setIsCouponValid(false);
      setCouponDiscount(0);
      onCouponApplied(0, 0);

      if (error.response && error.response.data && error.response.data.message) {
        if (error.response?.data?.message === "MINIMUM_CART_TOTAL_REQUIRED") {
          const minAmount = error.response.data.data.minCartTotal;
          setError(t('Minimum cart total required is') + ' ' + minAmount + '€');
        } else { 
          setError(t(error.response.data.message));
        }
      } else {
        setError(t('Invalid coupon code. Please try again.'));
      }
    } finally {
      setIsCouponLoading(false);
    }
  };

  return (
    <div className="bg-slate-200 dark:bg-stone-800 border border-slate-300 dark:border-stone-700 rounded-xl p-5 mb-7 shadow-sm shadow-slate-300 dark:shadow-none">
      <h2 className="text-lg md:text-xl font-semibold mb-4 text-stone-900 dark:text-orange-400">
        {t('Apply Coupon Code')}
      </h2>
      <div className="flex flex-col">
        <div className="flex flex-row gap-2">
          <div className="relative flex-grow">
            <input
              type="text"
              className={clsx(
                'w-full p-2 border rounded-lg focus:outline-none focus:ring-2 text-stone-900 dark:text-zinc-200 bg-slate-100 dark:bg-stone-700 border-slate-300 dark:border-stone-600',
                isCouponValid === true && 'border-green-500 focus:ring-green-500',
                isCouponValid === false && 'border-red-500 focus:ring-red-500',
                isCouponValid === null && 'focus:ring-slate-400 dark:focus:ring-stone-600'
              )}
              placeholder={t('Enter coupon code')}
              value={couponCode}
              onChange={e => setCouponCode(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isCouponLoading}
            />
            {isCouponLoading && (
              <div className="absolute right-3 top-3">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-500" />
              </div>
            )}
          </div>
          <Button
            variant="primary"
            className="px-3 whitespace-nowrap bg-orange-400 hover:bg-orange-500 dark:bg-orange-500 dark:hover:bg-orange-600"
            onClick={applyCoupon}
            disabled={isCouponLoading || couponCode.trim() === '' || isCouponValid === true}
          >
            {isCouponValid === true ? t('Applied') : t('Apply')}
          </Button>
        </div>

        {isCouponValid === true && couponDiscount > 0 && (
          <div className="text-green-500 mt-2">
            {t('Coupon applied! You saved')} {couponDiscount.toFixed(2)}€ {t("couponforturkish")}
          </div>
        )}

        {isCouponValid === false && !isCouponLoading && (
          <div className="text-sm text-red-500 mt-2">
            {error ? error : t('Invalid coupon code. Please try again.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponCode;
